/* eslint-disable react/no-unstable-nested-components */
import {
    ActionButton,
    Button,
    Card,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import {
    ColumnDef,
    getCoreRowModel,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table";
import * as React from "react";
import { useMemo, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import ListState from "../../../../components/ListState/ListState";
import { useDebounce } from "../../../../hooks/helpers";
import {
    displayNameInRightFormat,
    formatDate,
    convertDateFilterStringToDate,
    cn,
    removeEnumUnderscore,
    makeStringFirstLetterCapital,
    handleDisplayClientEmailCorrectly,
    appendMidnightTimeToDate,
} from "../../../../utils/helpers";
import Loader from "../../../../shared-ui/Loader/Loader";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import { getClientAge } from "../../../../utils/helpers/age";
import {
    useExportConsentReports,
    useGetConsentReports,
} from "../../hooks/queries/reports.queries";
import { useUpdateConsent } from "../../hooks/mutations/reports.mutations";
import {
    ConsentStatus,
    IConsentReport,
    IConsentDocument,
    DateSource,
} from "../../types/reports.types";
import useToast from "../../../../hooks/useToast";
import {
    getConsentStatusDisplay,
    getConsentStatusTag,
} from "../../helpers/reports.helper";
import EditIcon from "../../../../icons/Edit";
import CheckIcon from "../../../../icons/Check";
import TimesIcon from "../../../../icons/Times";
import EyeIcon from "../../../../icons/Eye";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import ActivityLogModal from "./ActivityLogModal";
import UploadIcon from "../../../../icons/Upload";
import AlertTraingleIcon from "../../../../icons/AlertTraingle";
import CreateConsentForm from "./CreateConsentForm";
import UploadDocumentForm from "./UploadDocumentForm";
import RequestConsentCorrectionForm from "./RequestConsentCorrectionForm";
import ConsentDocumentViewer from "../ConsentDocumentViewer/ConsentDocumentViewer";

type Dialogs = "create" | "renew" | "activity" | "upload" | "view";

export default function ConsentReportsList() {
    const consentColumnsVisibilityLs = localStorage.getItem(
        "consent_columns_visibility"
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const getInitialConsentColumnsVisibility = () => {
        if (!consentColumnsVisibilityLs) return {};
        const stored = JSON.parse(consentColumnsVisibilityLs);
        if (stored.actions === false) {
            delete stored.actions;
        }
        return stored;
    };

    const [columnVisibility, setColumnVisibility] = React.useState<
        Record<string, boolean>
    >(getInitialConsentColumnsVisibility);

    const searchString = searchParams.get("search") || "";

    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    // const [selectedConsentForRenewal, setSelectedConsentForRenewal] =
    //     React.useState<string | null>(null);
    const [showActivityLogModal, setShowActivityLogModal] =
        React.useState(false);
    const [selectedConsentForActivity, setSelectedConsentForActivity] =
        React.useState<IConsentReport | null>(null);
    const [showDocumentViewer, setShowDocumentViewer] = React.useState(false);
    const [selectedDocuments, setSelectedDocuments] = React.useState<
        IConsentDocument[]
    >([]);
    const [selectedConsentForViewer, setSelectedConsentForViewer] =
        React.useState<IConsentReport | null>(null);
    const [currentDocumentIndex, setCurrentDocumentIndex] = React.useState(0);
    const [selectedConsentForUpload, setSelectedConsentForUpload] =
        React.useState<string | null>(null);
    const [selectedConsentForCorrection, setSelectedConsentForCorrection] =
        React.useState<IConsentReport | null>(null);

    // Inline editing states
    const [editingConsentId, setEditingConsentId] = React.useState<
        string | null
    >(null);
    const [editedStartDate, setEditedStartDate] = React.useState<Date | null>(
        null
    );
    const [editedSignedDate, setEditedSignedDate] = React.useState<Date | null>(
        null
    );
    const [editedDateSent, setEditedDateSent] = React.useState<Date | null>(
        null
    );
    const [isExporting, setIsExporting] = React.useState(false);

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const updateConsentMutation = useUpdateConsent();

    const clientFilter = searchParams.get("client_id") || "";
    const providerFilter = searchParams.get("provider_id") || "";
    const startDateFromFilter = searchParams.get("start_date_from");
    const startDateToFilter = searchParams.get("start_date_to");
    const statusFilter = searchParams.get("status") || "";
    const sessionStatusFilter = searchParams.get("session_status") || "";
    // const needsRenewalFilter = searchParams.get("needs_renewal") === "true";
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const search = useDebounce(searchString, 500);

    const { data, error, isLoading, isSuccess } = useGetConsentReports({
        search_string: search,
        client_id: clientFilter,
        provider_id: providerFilter,
        status: statusFilter as ConsentStatus,
        start_date_from: startDateFromFilter || undefined,
        start_date_to: startDateToFilter || undefined,
        session_status: sessionStatusFilter,
        // needs_renewal: needsRenewalFilter || undefined,
        page: pageFilter,
        limit: limitFilter,
    });

    const isCreateConsentMutating = useIsMutating(["create-consent"]);
    // const isRenewConsentIsMutating = useIsMutating(["renew-consent"]);
    const isUploadDocumentMutating = useIsMutating([
        "upload-consent-documents",
    ]);
    const isUpdateConsentMutating = useIsMutating(["update-consent"]);

    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);
        searchParams.set("page", "1");
        localStorage.setItem("consentreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetStatusFilter = (status: string) => {
        if (status === "") searchParams.delete("status");
        else searchParams.set("status", status);
        searchParams.set("page", "1");
        localStorage.setItem("consentreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    // const handleSetNeedsRenewalFilter = (checked: boolean) => {
    //     if (checked) {
    //         searchParams.set("needs_renewal", "true");
    //     } else {
    //         searchParams.delete("needs_renewal");
    //     }
    //     searchParams.set("page", "1");
    //     localStorage.setItem("consentreports_filters", searchParams.toString());
    //     setSearchParams(searchParams);
    // };

    const handleSetStartDateFromFilter = (date: Date | null) => {
        if (date === null) searchParams.delete("start_date_from");
        else searchParams.set("start_date_from", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("consentreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetStartDateToFilter = (date: Date | null) => {
        if (date === null) searchParams.delete("start_date_to");
        else searchParams.set("start_date_to", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("consentreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        localStorage.setItem("consentreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        localStorage.setItem("consentreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const startDateFromFilterToDate =
        convertDateFilterStringToDate(startDateFromFilter);
    const startDateToFilterToDate =
        convertDateFilterStringToDate(startDateToFilter);

    const isApproachingDeadline = (startDate?: string) => {
        if (!startDate) return false;
        const start = new Date(startDate);
        const today = new Date();
        const daysUntil = Math.ceil(
            (start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        return daysUntil <= 3 && daysUntil >= 0;
    };

    // const isEligibleForRenewal = (consent: IConsentReport) => {
    //     if (
    //         consent.status !== ConsentStatus.COMPLETED ||
    //         !consent.signed_date ||
    //         !consent.client.client_date_of_birth
    //     ) {
    //         return false;
    //     }

    //     // Calculate client age when signed and current age
    //     const signedDate = new Date(consent.signed_date);
    //     const clientDob = new Date(consent.client.client_date_of_birth);
    //     const currentAge = getClientAge(consent.client.client_date_of_birth);

    //     // Age when signed = signed year - birth year
    //     const ageWhenSigned =
    //         signedDate.getFullYear() - clientDob.getFullYear();
    //     const birthdayThisYear = new Date(
    //         signedDate.getFullYear(),
    //         clientDob.getMonth(),
    //         clientDob.getDate()
    //     );
    //     const adjustedAgeWhenSigned =
    //         signedDate >= birthdayThisYear ? ageWhenSigned : ageWhenSigned - 1;

    //     // Client was <18 when signed AND is now 18+
    //     return adjustedAgeWhenSigned < 18 && currentAge >= 18;
    // };

    // const handleDisplayRenewConsentDialog = (consentId: string) => {
    //     setSelectedConsentForRenewal(consentId);
    //     setDialogInView("renew");
    // };

    const handleViewActivityLog = (consent: IConsentReport) => {
        setSelectedConsentForActivity(consent);
        setShowActivityLogModal(true);
    };

    const handleViewDocuments = (consent: IConsentReport) => {
        if (consent.consent_documents.length > 0) {
            setSelectedDocuments(consent.consent_documents);
            setSelectedConsentForViewer(consent);
            setCurrentDocumentIndex(0);
            setShowDocumentViewer(true);
        }
    };

    const handleCloseDocumentViewer = () => {
        setShowDocumentViewer(false);
        setSelectedDocuments([]);
        setSelectedConsentForViewer(null);
        setCurrentDocumentIndex(0);
    };

    const handleOpenUploadModal = (consentId: string) => {
        setSelectedConsentForUpload(consentId);
        setDialogInView("upload");
    };

    const handleCloseUploadModal = () => {
        setDialogInView(null);
        setSelectedConsentForUpload(null);
    };

    const handleEditConsent = useCallback((consent: IConsentReport) => {
        setEditingConsentId(consent.consent_id);
        setEditedStartDate(
            consent.start_date
                ? convertDateFilterStringToDate(consent.start_date)
                : null
        );
        setEditedSignedDate(
            consent.signed_date
                ? convertDateFilterStringToDate(consent.signed_date)
                : null
        );
        setEditedDateSent(
            consent.consent_form_sent_at
                ? convertDateFilterStringToDate(consent.consent_form_sent_at)
                : null
        );
    }, []);

    const handleCancelEdit = () => {
        setEditingConsentId(null);
        setEditedStartDate(null);
        setEditedSignedDate(null);
        setEditedDateSent(null);
    };

    const handleSaveEdit = useCallback(
        (originalConsent: IConsentReport) => {
            const updateData: Record<string, string | null> = {};

            const originalStartDate =
                originalConsent.start_date?.split("T")[0] || "";
            const originalSignedDate =
                originalConsent.signed_date?.split("T")[0] || "";

            const originalDateSent =
                originalConsent.consent_form_sent_at?.split("T")[0] || "";

            const editedStartDateStr = editedStartDate
                ? editedStartDate.toISOString().split("T")[0]
                : "";
            const editedSignedDateStr = editedSignedDate
                ? editedSignedDate.toISOString().split("T")[0]
                : "";
            const editedDateSentStr = editedDateSent
                ? editedDateSent.toISOString().split("T")[0]
                : "";

            // Only update fields that changed
            if (editedStartDateStr !== originalStartDate) {
                if (editedStartDateStr) {
                    updateData.start_date = editedStartDateStr;
                    updateData.start_date_source = "manual";
                }
            }

            if (editedSignedDateStr !== originalSignedDate) {
                if (editedSignedDateStr) {
                    updateData.signed_date = editedSignedDateStr;
                }
            }

            if (editedDateSentStr !== originalDateSent) {
                updateData.consent_form_sent_at = editedDateSentStr || null;
            }

            if (Object.keys(updateData).length === 0) {
                // No changes, just exit edit mode
                handleCancelEdit();
                return;
            }

            updateConsentMutation.mutate(
                {
                    consentId: originalConsent.consent_id,
                    data: updateData,
                },
                {
                    onSuccess: (response) => {
                        queryClient.invalidateQueries(["consent-reports"]);
                        toast({
                            mode: "success",
                            message:
                                response.message ||
                                "Consent updated successfully",
                        });
                        handleCancelEdit();
                    },
                    onError: (err) => {
                        toast({
                            mode: "error",
                            message:
                                err.response?.data.message ||
                                "Could not update consent at this time",
                        });
                    },
                }
            );
        },
        [
            updateConsentMutation,
            queryClient,
            toast,
            editedStartDate,
            editedSignedDate,
            editedDateSent,
        ]
    );

    const handleChangeConsentStatus = useCallback(
        (consent: IConsentReport, status: ConsentStatus) => {
            if (status === ConsentStatus.NEEDS_CORRECTION) {
                setSelectedConsentForCorrection(consent);
                return;
            }
            const changeStatusPayload = {
                consentId: consent.consent_id,
                data: { status },
            };
            updateConsentMutation.mutate(changeStatusPayload, {
                onSuccess: (response) => {
                    queryClient.invalidateQueries(["consent-reports"]);
                    toast({
                        mode: "success",
                        message:
                            response.message ||
                            "Consent status updated successfully",
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update consent status at this time",
                    });
                },
            });
        },
        [updateConsentMutation, queryClient, toast]
    );

    const handleColumnVisibilityChange = (
        updaterOrValue:
            | VisibilityState
            | ((old: VisibilityState) => VisibilityState)
    ) => {
        setColumnVisibility((prevState) => {
            let newState: VisibilityState;

            if (typeof updaterOrValue === "function") {
                newState = updaterOrValue(prevState);
            } else {
                newState = updaterOrValue;
            }

            localStorage.setItem(
                "consent_columns_visibility",
                JSON.stringify(newState)
            );

            return newState;
        });
    };

    const columns: ColumnDef<IConsentReport>[] = useMemo(
        () => [
            {
                accessorKey: "client.client_first_name",
                header: "Client Name",
                cell: ({ row }) => {
                    const { client } = row.original;
                    return (
                        <Link
                            to={`/clients/${row.original.client_id}`}
                            className="font-medium text-textLink underline"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {displayNameInRightFormat({
                                firstName: client.client_first_name,
                                lastName: client.client_last_name,
                            })}
                        </Link>
                    );
                },
            },
            {
                accessorKey: "client.client_phone",
                header: "Phone",
                cell: ({ row }) => row.original.client.client_phone || "--",
            },
            {
                accessorKey: "client.client_email",
                header: "Email",
                cell: ({ row }) => (
                    <span className="truncate max-w-[200px] block">
                        {handleDisplayClientEmailCorrectly(
                            row.original.client.client_email
                        )}
                    </span>
                ),
            },
            {
                accessorKey: "client.client_date_of_birth",
                header: "Date of Birth",
                cell: ({ row }) => {
                    const dob = row.original.client.client_date_of_birth;
                    if (!dob) return "--";

                    const age = getClientAge(dob);
                    return (
                        <div className="flex flex-col">
                            <span>
                                {formatDate(
                                    appendMidnightTimeToDate(dob),
                                    true
                                )}
                            </span>
                            <span className="text-sm text-gray-500">
                                ({age} years)
                            </span>
                        </div>
                    );
                },
            },
            {
                accessorKey: "provider.provider_first_name",
                header: "Provider Name",
                cell: ({ row }) => {
                    const { provider } = row.original;
                    return (
                        <div className="flex flex-col">
                            <span className="font-medium">
                                {displayNameInRightFormat({
                                    firstName: provider.provider_first_name,
                                    lastName: provider.provider_last_name,
                                })}
                            </span>
                            <span className="text-sm text-gray-500">
                                {makeStringFirstLetterCapital(
                                    removeEnumUnderscore(
                                        row.original.session.recurrence_rule
                                    )
                                )}{" "}
                                •{" "}
                                {makeStringFirstLetterCapital(
                                    removeEnumUnderscore(
                                        row.original.session.delivery_option
                                    )
                                )}
                            </span>
                        </div>
                    );
                },
            },
            {
                accessorKey: "start_date",
                header: "Start Date",
                cell: ({ row }) => {
                    const isApproaching = isApproachingDeadline(
                        row.original.start_date
                    );
                    const isEditing =
                        editingConsentId === row.original.consent_id;

                    return isEditing ? (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            role="button"
                            tabIndex={0}
                            className="w-32"
                        >
                            <DatePicker
                                selected={editedStartDate}
                                onChange={(date) => setEditedStartDate(date)}
                                placeholderText="MM/DD/YYYY"
                                isClearable
                                isInDialog
                            />
                        </div>
                    ) : (
                        <div
                            className={cn(`flex items-center gap-2`, {
                                "bg-[--color-warning-bg] px-2 py-1 rounded":
                                    isApproaching,
                            })}
                        >
                            <span>
                                {row.original.start_date
                                    ? formatDate(
                                          appendMidnightTimeToDate(
                                              row.original.start_date
                                          ),
                                          true
                                      )
                                    : "--"}
                            </span>
                            {isApproaching && (
                                <AlertTraingleIcon stroke="var(--color-warning-text)" />
                            )}
                        </div>
                    );
                },
            },
            {
                accessorKey: "status",
                header: "Consent Status",
                cell: ({ row }) => (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton
                                className={cn(
                                    `capitalize !min-h-7 ![border:none] px-2 ${getConsentStatusTag(
                                        row.original.status
                                    )}`
                                )}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {getConsentStatusDisplay(row.original.status)}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {Object.values(ConsentStatus)
                                .filter(
                                    (status) => status !== row.original.status
                                )
                                .map((status) => (
                                    <DropdownItem
                                        key={status}
                                        onClick={(e) => {
                                            handleChangeConsentStatus(
                                                row.original,
                                                status
                                            );
                                            e.stopPropagation();
                                        }}
                                    >
                                        {getConsentStatusDisplay(status)}
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                ),
            },
            {
                accessorKey: "consent_form_sent_at",
                header: "Date Sent",
                cell: ({ row }) => {
                    const editableStatuses = [
                        ConsentStatus.SENT,
                        ConsentStatus.RECEIVED,
                        ConsentStatus.NEEDS_CORRECTION,
                        ConsentStatus.COMPLETED,
                    ];

                    const isEditing =
                        editingConsentId === row.original.consent_id &&
                        row.original.start_date_source !== DateSource.INTAKE &&
                        editableStatuses.includes(row.original.status);

                    return isEditing ? (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            role="button"
                            tabIndex={0}
                            className="w-32"
                        >
                            <DatePicker
                                selected={editedDateSent}
                                onChange={(date) => setEditedDateSent(date)}
                                placeholderText="MM/DD/YYYY"
                                isClearable
                                isInDialog
                            />
                        </div>
                    ) : (
                        <span>
                            {row.original.consent_form_sent_at
                                ? formatDate(
                                      appendMidnightTimeToDate(
                                          row.original.consent_form_sent_at
                                      ),
                                      true
                                  )
                                : "--"}
                        </span>
                    );
                },
            },
            {
                accessorKey: "intake_consent_form_rechecked_at",
                header: "Rechecked: Good form on file",
                cell: ({ row }) =>
                    row.original.intake_consent_form_rechecked_at
                        ? formatDate(
                              appendMidnightTimeToDate(
                                  row.original.intake_consent_form_rechecked_at
                              ),
                              true
                          )
                        : "--",
            },
            {
                accessorKey: "signed_date",
                header: "Signed Date",
                cell: ({ row }) => {
                    const signedDate = row.original.signed_date;
                    const isEditing =
                        editingConsentId === row.original.consent_id;

                    return isEditing ? (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            role="button"
                            tabIndex={0}
                            className="w-32"
                        >
                            <DatePicker
                                selected={editedSignedDate}
                                onChange={(date) => setEditedSignedDate(date)}
                                placeholderText="MM/DD/YYYY"
                                isClearable
                                isInDialog
                            />
                        </div>
                    ) : (
                        <span>
                            {signedDate
                                ? formatDate(
                                      appendMidnightTimeToDate(signedDate),
                                      true
                                  )
                                : "Not signed"}
                        </span>
                    );
                },
            },
            {
                accessorKey: "consent_documents",
                header: "Documents Count",
                cell: ({ row }) => {
                    const docs = row.original.consent_documents;
                    return docs.length > 0 ? (
                        <Button
                            variant="normal"
                            size="auto"
                            className="p-0 underline font-normal text-textLink flex items-center gap-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewDocuments(row.original);
                            }}
                        >
                            <span>
                                {docs.length} document
                                {docs.length !== 1 ? "s" : ""}
                            </span>
                            <span className="text-green-600">📄</span>
                        </Button>
                    ) : (
                        <span className="text-gray-500">No documents</span>
                    );
                },
            },
            {
                accessorKey: "updated_at",
                header: "Updated Date",
                cell: ({ row }) => formatDate(row.original.updated_at),
            },
            {
                accessorKey: "actions",
                header: "Actions",
                cell: ({ row }) => {
                    const consent = row.original;
                    // const eligibleForRenewal = isEligibleForRenewal(consent);
                    const isEditing = editingConsentId === consent.consent_id;

                    return (
                        <div className="flex gap-2 items-center">
                            {isEditing ? (
                                <>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelEdit();
                                        }}
                                        disabled={
                                            updateConsentMutation.isLoading
                                        }
                                        actionTitle="Cancel"
                                    >
                                        <TimesIcon className="stroke-danger size-3.5" />
                                    </ActionButton>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSaveEdit(consent);
                                        }}
                                        disabled={
                                            updateConsentMutation.isLoading
                                        }
                                        actionTitle="Save"
                                    >
                                        <CheckIcon className="stroke-primary size-3.5" />
                                    </ActionButton>
                                </>
                            ) : (
                                <>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditConsent(consent);
                                        }}
                                        actionTitle="Edit dates"
                                    >
                                        <EditIcon className="stroke-text" />
                                    </ActionButton>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewActivityLog(consent);
                                        }}
                                        actionTitle="View activities"
                                    >
                                        <EyeIcon className="stroke-text" />
                                    </ActionButton>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenUploadModal(
                                                consent.consent_id
                                            );
                                        }}
                                        actionTitle="Upload documents"
                                    >
                                        <UploadIcon className="stroke-text" />
                                    </ActionButton>
                                    {/* {eligibleForRenewal && (
                                        <>
                                            <Button
                                                variant="secondary"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDisplayRenewConsentDialog(
                                                        consent.consent_id
                                                    );
                                                }}
                                                className="text-xs !h-8"
                                            >
                                                Renew
                                            </Button>
                                            <span
                                                className="text-xs text-[--color-warning-text] bg-[--color-warning-bg] px-2 py-1 rounded flex items-center gap-x-1"
                                                title="Client was a minor when consent was signed and is now 18+"
                                            >
                                                <AlertTraingleIcon />
                                                Needs Renewal
                                            </span>
                                        </>
                                    )} */}
                                </>
                            )}
                        </div>
                    );
                },
            },
        ],
        [
            editingConsentId,
            editedStartDate,
            editedSignedDate,
            editedDateSent,
            updateConsentMutation.isLoading,
            handleChangeConsentStatus,
            handleSaveEdit,
            handleEditConsent,
        ]
    );

    const table = useReactTable({
        columns,
        data: data?.data || [],
        state: {
            columnVisibility,
        },
        getCoreRowModel: getCoreRowModel(),
        onColumnVisibilityChange: handleColumnVisibilityChange,
    });

    const selectedColumnsForExport = table
        .getAllLeafColumns()
        .filter((column) => column.getIsVisible())
        .filter(
            (column) =>
                column.id !== "actions" &&
                column?.columnDef?.header?.toString()?.toLowerCase() !==
                    "actions"
        )
        .map(
            (column) =>
                column?.columnDef?.header?.toString()?.toUpperCase() ||
                column.id?.replaceAll("_", " ")?.toUpperCase()
        );

    const { refetch: exportData } = useExportConsentReports(
        {
            search_string: search,
            client_id: clientFilter,
            provider_id: providerFilter,
            status: statusFilter as ConsentStatus,
            start_date_from: startDateFromFilter || undefined,
            start_date_to: startDateToFilter || undefined,
            session_status: sessionStatusFilter,
            selected_columns:
                selectedColumnsForExport && selectedColumnsForExport.length > 0
                    ? [
                          ...selectedColumnsForExport.filter(
                              (col) => col !== "Actions"
                          ),
                          "CONSULT CALL NOTES",
                      ]
                    : [],
            // needs_renewal: needsRenewalFilter || undefined,
        },
        false
    );

    const handleExport = async () => {
        setIsExporting(true);
        try {
            await exportData();
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            {selectedConsentForUpload ? (
                <Dialog open={dialogInView === "upload"}>
                    <DialogContent
                        variant="center"
                        title="Upload Consent Documents"
                        handleCloseDialog={handleCloseUploadModal}
                        saveText="Upload"
                        cancelText="Cancel"
                        isSubmitBtnDisabled={isUploadDocumentMutating > 0}
                        isCancelBtnDisabled={isUploadDocumentMutating > 0}
                        submitBtnFormValue="upload-consent-documents"
                    >
                        <UploadDocumentForm
                            consentId={selectedConsentForUpload}
                            onFormSubmit={handleCloseUploadModal}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <Dialog open={dialogInView === "create"}>
                <DialogContent
                    variant="center"
                    title="Track New Consent"
                    handleCloseDialog={() => setDialogInView(null)}
                    saveText="Create Consent"
                    cancelText="Cancel"
                    isSubmitBtnDisabled={isCreateConsentMutating > 0}
                    isCancelBtnDisabled={isCreateConsentMutating > 0}
                    submitBtnFormValue="create-consent-form"
                >
                    <CreateConsentForm
                        onFormSubmit={() => setDialogInView(null)}
                    />
                </DialogContent>
            </Dialog>
            {/* {selectedConsentForRenewal ? (
                <Dialog open={dialogInView === "renew"}>
                    <DialogContent
                        variant="center"
                        title="Renew Consent"
                        handleCloseDialog={() => setDialogInView(null)}
                        saveText="Create Renewal"
                        submitBtnFormValue="renew-consent-form"
                        cancelText="Cancel"
                        isSubmitBtnDisabled={isRenewConsentIsMutating > 0}
                        isCancelBtnDisabled={isRenewConsentIsMutating > 0}
                    >
                        <RenewConsentForm
                            consentId={selectedConsentForRenewal}
                            onFormSubmit={() => setDialogInView(null)}
                        />
                    </DialogContent>
                </Dialog>
            ) : null} */}
            <div>
                <Card classNames="flex flex-col gap-[--spacing-items] mb-[--spacing-header]">
                    <div className="flex justify-between items-center">
                        <SearchInput
                            placeholder="Search by client name, email, phone"
                            containerClass="w-[400px]"
                            onChange={handleSetSearchString}
                            defaultValue={searchString}
                        />
                        <div className="flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={handleExport}
                                disabled={
                                    isExporting || data?.data.length === 0
                                }
                            >
                                {isExporting ? "Exporting..." : "Export"}
                            </Button>
                            <Button onClick={() => setDialogInView("create")}>
                                Track New Consent
                            </Button>
                        </div>
                    </div>

                    <div className="flex items-center gap-[--spacing-items] flex-wrap">
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    {statusFilter
                                        ? getConsentStatusDisplay(
                                              statusFilter as ConsentStatus
                                          )
                                        : "Status"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width={150}>
                                <DropdownItem
                                    onClick={() => handleSetStatusFilter("")}
                                    isSelected={!statusFilter}
                                    closeMenuOnSelect
                                >
                                    All Statuses
                                </DropdownItem>
                                {Object.values(ConsentStatus).map((status) => (
                                    <DropdownItem
                                        key={status}
                                        onClick={() =>
                                            handleSetStatusFilter(status)
                                        }
                                        isSelected={statusFilter === status}
                                        closeMenuOnSelect
                                    >
                                        {getConsentStatusDisplay(status)}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>

                        <Popover>
                            <PopoverTrigger asChild>
                                <FilterButton>Start Date Range</FilterButton>
                            </PopoverTrigger>
                            <PopoverContent align="end">
                                <div className="mb-1">
                                    <DatePicker
                                        label="From"
                                        placeholderText="MM/DD/YYYY"
                                        selected={startDateFromFilterToDate}
                                        onChange={handleSetStartDateFromFilter}
                                        maxDate={
                                            startDateToFilterToDate ||
                                            new Date(Date.now())
                                        }
                                        isClearable
                                        isInDialog
                                    />
                                </div>
                                <DatePicker
                                    label="To"
                                    placeholderText="MM/DD/YYYY"
                                    selected={startDateToFilterToDate}
                                    onChange={handleSetStartDateToFilter}
                                    className="hover:border-primary"
                                    minDate={startDateFromFilterToDate}
                                    isClearable
                                    isInDialog
                                />
                            </PopoverContent>
                        </Popover>

                        {/* Needs Renewal Filter */}
                        {/* <label
                            htmlFor="needs-renewal-checkbox"
                            className="flex items-center gap-2 cursor-pointer"
                        >
                            <input
                                id="needs-renewal-checkbox"
                                type="checkbox"
                                checked={needsRenewalFilter}
                                onChange={(e) =>
                                    handleSetNeedsRenewalFilter(
                                        e.target.checked
                                    )
                                }
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Needs Renewal
                            </span>
                        </label> */}
                    </div>
                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "status",
                                values: statusFilter
                                    ? [
                                          getConsentStatusDisplay(
                                              statusFilter as ConsentStatus
                                          ),
                                      ]
                                    : [],
                            },
                            // {
                            //     key: "needs_renewal",
                            //     values: needsRenewalFilter
                            //         ? ["Needs Renewal"]
                            //         : [],
                            // },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                        localStorageKey="consentreports_filters"
                    />
                </Card>

                {isLoading && <Loader />}

                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            "Cannot display consent reports at this time please try again later"
                        }
                    />
                )}

                {isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="No consent records match your current filters. Try adjusting your search criteria or create a new consent record."
                        isError={false}
                        emptyMessage="No consent records found"
                        emptyBtnProps={{
                            showButton: true,
                            onButtonClick: () => setDialogInView("create"),
                            buttonText: "Track New Consent",
                        }}
                    />
                )}

                {isSuccess && data.data.length > 0 && (
                    <Table
                        columns={columns}
                        data={data.data}
                        hasPagination={data.total_count > limitFilter}
                        showSizeFilter
                        isColumnVisible={columnVisibility}
                        showColumnVisibilityFilter
                        columnVisibilityDropdownItems={
                            <>
                                {table.getAllLeafColumns().map((column) => {
                                    if (column.id === "actions") return null;
                                    return (
                                        <DropdownItem
                                            key={column.id}
                                            onClick={() =>
                                                column.toggleVisibility()
                                            }
                                            className="*:!uppercase"
                                            isMulti
                                            itemValue={column.id}
                                            values={table
                                                .getVisibleLeafColumns()
                                                .map((col) => col.id)}
                                        >
                                            {column?.columnDef?.header?.toString() ||
                                                column.id?.replaceAll("_", " ")}
                                        </DropdownItem>
                                    );
                                })}
                            </>
                        }
                        pagination={
                            <Pagination
                                totalCount={data.total_count}
                                totalPages={data.total_pages}
                                currentPage={pageFilter}
                                onPageChange={handlePageChange}
                                limit={limitFilter}
                                onLimitChange={handleLimitChange}
                            />
                        }
                    />
                )}
            </div>

            {showActivityLogModal && (
                <ActivityLogModal
                    isOpen={showActivityLogModal}
                    consent={selectedConsentForActivity}
                    onClose={() => {
                        setShowActivityLogModal(false);
                        setSelectedConsentForActivity(null);
                    }}
                />
            )}

            {selectedConsentForCorrection ? (
                <Dialog open>
                    <DialogContent
                        variant="center"
                        title="Request Consent Correction"
                        handleCloseDialog={() =>
                            setSelectedConsentForCorrection(null)
                        }
                        saveText="Send Correction Request"
                        cancelText="Cancel"
                        submitBtnFormValue="request-consent-correction"
                        isCancelBtnDisabled={isUpdateConsentMutating > 0}
                        isSubmitBtnDisabled={isUpdateConsentMutating > 0}
                    >
                        <RequestConsentCorrectionForm
                            consentId={selectedConsentForCorrection.consent_id}
                            onFormSubmit={() =>
                                setSelectedConsentForCorrection(null)
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}

            {/* Document Viewer Modal */}
            <Dialog
                open={showDocumentViewer}
                onOpenChange={handleCloseDocumentViewer}
            >
                <DialogContent
                    variant="drawer"
                    title="Consent document"
                    handleCloseDialog={handleCloseDocumentViewer}
                    showFooter={false}
                    wrapperClass="w-[1180px] max-w-[calc(100vw-24px)]"
                    classNames="!overflow-hidden !p-0"
                >
                    {selectedDocuments.length > 0 &&
                    selectedConsentForViewer ? (
                        <ConsentDocumentViewer
                            consent={selectedConsentForViewer}
                            document={selectedDocuments[currentDocumentIndex]}
                            documentIndex={currentDocumentIndex}
                            documentCount={selectedDocuments.length}
                            onPreviousDocument={() =>
                                setCurrentDocumentIndex((previous) =>
                                    previous > 0
                                        ? previous - 1
                                        : selectedDocuments.length - 1
                                )
                            }
                            onNextDocument={() =>
                                setCurrentDocumentIndex((previous) =>
                                    previous < selectedDocuments.length - 1
                                        ? previous + 1
                                        : 0
                                )
                            }
                        />
                    ) : (
                        <div className="flex items-center justify-center h-64 text-gray-500">
                            No documents available
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
